import { collection, doc, setDoc, getDoc, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Assignment, AssignmentStudentStatus, ClassMember, AssignmentStatusValue } from '../types/alarm';
import { classMembersService } from './classMembersService';
import { optimizedAssignmentServiceV3 } from './optimizedAssignmentServiceV3';
import { GeneralAssignmentConsolidatedService } from './generalAssignmentConsolidatedService';
import { assignmentMemoService } from './assignmentMemoService';

function coerceToDate(value: any): Date {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'object' && typeof value.toDate === 'function') return value.toDate(); // Firestore Timestamp
  return new Date(value);
}

export const assignmentService = {
  async createAssignment(userId: string, classId: string, assignment: Assignment, members: ClassMember[], memberGroup?: string): Promise<void> {
    // 새로운 통합 구조 사용
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId, memberGroup);
    
    try {
      await consolidatedService.createGeneralAssignment(
        assignment,
        assignment.title,
        assignment.description || '',
        coerceToDate(assignment.dueDate),
        assignment.basePoints,
        assignment.assignedStudentIds || members.map(m => m.id)
      );
      console.log('✅ 새로운 구조: 일반 과제 생성 완료');
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 생성 실패:', error);
      throw error;
    }
  },

  async upsertAssignment(userId: string, classId: string, assignment: Assignment, members: ClassMember[], memberGroup?: string): Promise<void> {
    // 새로운 통합 구조 사용
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId, memberGroup);
    
    try {
      // 기존 과제가 있는지 확인
      const existingAssignment = await consolidatedService.loadGeneralAssignment(assignment.id);
      
      if (existingAssignment) {
        await consolidatedService.updateGeneralAssignment(
          assignment.id,
          assignment.title,
          assignment.description || '',
          coerceToDate(assignment.dueDate),
          assignment.basePoints,
          assignment.assignedStudentIds || members.map(m => m.id),
          assignment.categoryId,
          assignment.isActive,
          assignment.icon,
          assignment.notes,
          assignment.rewardEnabled,
          assignment.rewardCode,
          assignment.rewardMessage,
          assignment.rewardCoins,
          assignment.rewardItemCardIds,
          assignment.rewardGrantedStudentIds
        );
        console.log('✅ 새로운 구조: 일반 과제 업데이트 완료');
      } else {
        await consolidatedService.createGeneralAssignment(
          assignment,
          assignment.title,
          assignment.description || '',
          coerceToDate(assignment.dueDate),
          assignment.basePoints,
          assignment.assignedStudentIds || members.map(m => m.id)
        );
        console.log('✅ 새로운 구조: 일반 과제 생성 완료');
      }
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 upsert 실패:', error);
      throw error;
    }
  },

  async loadAssignments(userId: string, classId: string, memberGroup?: string): Promise<Assignment[]> {
    // 새로운 통합 구조 사용
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId, memberGroup);
    
    try {
      const assignmentInfos = await consolidatedService.loadAllGeneralAssignments();
      
      // GeneralAssignmentInfo를 Assignment로 변환 (studentStatuses 포함)
      const assignments: Assignment[] = assignmentInfos.map(info => {
        // pendingCount와 submittedCount 계산
        // 제출 확인 상태도 미확인으로 취급
        const pendingCount = info.studentStatuses.filter(s => s.status === 'pending' || s.status === 'submitted').length;
        const submittedCount = info.studentStatuses.filter(s => s.status === 'submitted').length;
        
        const assignment: Assignment = {
          id: info.assignmentId,
          title: info.title,
          description: info.description,
          dueDate: new Date(info.dueDate),
          basePoints: info.basePoints,
          assignedStudentIds: info.assignedStudentIds,
          createdAt: info.createdAt instanceof Date ? info.createdAt : 
                     (info.createdAt as any)?.toDate?.() || 
                     (typeof info.createdAt === 'string' ? new Date(info.createdAt) : new Date()),
          updatedAt: (info.updatedAt || info.lastUpdated) instanceof Date ? (info.updatedAt || info.lastUpdated) : 
                     ((info.updatedAt || info.lastUpdated) as any)?.toDate?.() || 
                     (typeof (info.updatedAt || info.lastUpdated) === 'string' ? new Date(info.updatedAt || info.lastUpdated) : new Date()),
          isActive: info.isActive,
          categoryId: info.categoryId,
          icon: info.icon || '',
          notes: info.notes || '',
          pendingCount,
          submittedCount,
          lastPendingUpdate: new Date(),
          lastSubmittedUpdate: new Date()
        };
        
        // 과제 보상 필드 추가 (rewardEnabled가 true일 때만)
        if (info.rewardEnabled === true) {
          assignment.rewardEnabled = true;
          if (info.rewardCode !== undefined) {
            assignment.rewardCode = info.rewardCode;
          }
          if (info.rewardMessage !== undefined) {
            assignment.rewardMessage = info.rewardMessage;
          }
          if (info.rewardCoins !== undefined) {
            assignment.rewardCoins = info.rewardCoins;
          }
          if (info.rewardItemCardIds !== undefined) {
            assignment.rewardItemCardIds = info.rewardItemCardIds;
          }
          if (info.rewardGrantedStudentIds !== undefined) {
            assignment.rewardGrantedStudentIds = info.rewardGrantedStudentIds;
          }
          
          console.log('🎁 과제 보상 정보 포함:', {
            assignmentId: info.assignmentId,
            title: info.title,
            rewardCode: info.rewardCode,
            rewardMessage: info.rewardMessage,
            rewardCoins: info.rewardCoins,
            rewardItemCardIds: info.rewardItemCardIds,
            rewardGrantedStudentIds: info.rewardGrantedStudentIds
          });
        }
        
        return assignment;
      });
      
      console.log('✅ 새로운 구조: 일반 과제 목록 로드 완료 (카운트 포함):', assignments.length);
      return assignments;
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 목록 로드 실패:', error);
      throw error;
    }
  },

  async loadAssignmentWithStatuses(userId: string, classId: string, assignmentId: string): Promise<{ assignment: Assignment | null; statuses: AssignmentStudentStatus[] }>{
    // 새로운 통합 구조 사용
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId);
    
    try {
      const assignmentInfo = await consolidatedService.loadGeneralAssignment(assignmentId);
      
      if (!assignmentInfo) {
        return { assignment: null, statuses: [] };
      }

      // GeneralAssignmentInfo를 Assignment로 변환
      const assignment: Assignment = {
        id: assignmentInfo.assignmentId,
        title: assignmentInfo.title,
        description: assignmentInfo.description,
        dueDate: new Date(assignmentInfo.dueDate),
        basePoints: assignmentInfo.basePoints,
        assignedStudentIds: assignmentInfo.assignedStudentIds,
        createdAt: assignmentInfo.createdAt,
        updatedAt: assignmentInfo.updatedAt || assignmentInfo.lastUpdated,
        isActive: assignmentInfo.isActive,
        categoryId: assignmentInfo.categoryId,
        icon: assignmentInfo.icon || '',
        notes: assignmentInfo.notes || ''
      };
      
      // 과제 보상 필드 추가 (rewardEnabled가 true일 때만)
      if (assignmentInfo.rewardEnabled === true) {
        assignment.rewardEnabled = true;
        if (assignmentInfo.rewardCode !== undefined) {
          assignment.rewardCode = assignmentInfo.rewardCode;
        }
        if (assignmentInfo.rewardMessage !== undefined) {
          assignment.rewardMessage = assignmentInfo.rewardMessage;
        }
        if (assignmentInfo.rewardCoins !== undefined) {
          assignment.rewardCoins = assignmentInfo.rewardCoins;
        }
        if (assignmentInfo.rewardItemCardIds !== undefined) {
          assignment.rewardItemCardIds = assignmentInfo.rewardItemCardIds;
        }
        if (assignmentInfo.rewardGrantedStudentIds !== undefined) {
          assignment.rewardGrantedStudentIds = assignmentInfo.rewardGrantedStudentIds;
        }
        
        console.log('✅ 과제 보상 정보 로드:', {
          assignmentId,
          rewardCode: assignmentInfo.rewardCode,
          rewardMessage: assignmentInfo.rewardMessage,
          rewardCoins: assignmentInfo.rewardCoins,
          rewardItemCardIds: assignmentInfo.rewardItemCardIds,
          rewardGrantedStudentIds: assignmentInfo.rewardGrantedStudentIds
        });
      }

      const memoMap = await assignmentMemoService.getMemoMapForAssignment(userId, classId, assignmentId);

      // 멤버 상태들을 AssignmentStudentStatus 형태로 변환
      const statuses: AssignmentStudentStatus[] = assignmentInfo.studentStatuses.map(status => {
        const statusAny = status as any;
        const memoEntry = memoMap?.[status.studentId];
        return {
          id: status.studentId,
          studentId: status.studentId,
          assignmentId: assignmentId,
          status: status.status,
          earnedPoints: status.points,
          memo: memoEntry?.memo || status.memo || '',
          submissionLink: statusAny.submissionLink,
          submissionMessage: statusAny.submissionMessage,
          submissionImagePath: statusAny.submissionImagePath,
          submissionImageUpdatedAt: statusAny.submissionImageUpdatedAt,
          submissionImageSizeBytes: statusAny.submissionImageSizeBytes,
          submittedAt: statusAny.submittedAt,
          updatedAt: status.updatedAt
        };
      });

      console.log('✅ 새로운 구조: 일반 과제 및 상태 로드 완료:', assignmentId);
      return { assignment, statuses };
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 및 상태 로드 실패:', error);
      throw error;
    }
  },

  async updateStudentStatus(userId: string, classId: string, assignmentId: string, studentId: string, partial: Partial<AssignmentStudentStatus>): Promise<void> {
    // 새로운 통합 구조 사용
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId);
    
    try {
      await assignmentMemoService.saveMemo(
        userId,
        classId,
        assignmentId,
        studentId,
        (partial.memo || '').toString()
      );
      await consolidatedService.updateStudentStatus(
        assignmentId,
        studentId,
        partial.status || 'pending',
        partial.earnedPoints || 0,
        ''
      );
      console.log('✅ 새로운 구조: 일반 과제 멤버 상태 업데이트 완료');
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  },

  // 여러 멤버의 상태를 배치로 업데이트 (성능 최적화)
  async batchUpdateStudentStatuses(
    userId: string, 
    classId: string, 
    assignmentId: string, 
    studentUpdates: Array<{
      studentId: string;
      status: AssignmentStatusValue;
      earnedPoints: number;
      memo?: string;
    }>
  ): Promise<void> {
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId);
    
    try {
      await assignmentMemoService.batchSaveMemos(
        userId,
        classId,
        studentUpdates.map(update => ({
          assignmentKey: assignmentId,
          studentId: update.studentId,
          memo: update.memo || ''
        }))
      );
      // memo는 assignmentMemo 컬렉션에서 관리하므로 제외
      await consolidatedService.batchUpdateStudentStatuses(
        assignmentId,
        studentUpdates.map(update => ({
          studentId: update.studentId,
          status: update.status,
          points: update.earnedPoints
        }))
      );
      
      console.log('✅ 새로운 구조: 일반 과제 멤버 상태 배치 업데이트 완료');
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 멤버 상태 배치 업데이트 실패:', error);
      throw error;
    }
  },

  async syncStatusesWithMembers(userId: string, classId: string, assignmentId: string, currentMembers: ClassMember[]): Promise<void> {
    // V3 구조에서는 과제의 assignedStudentIds와 동기화
    const assignmentRef = doc(db, 'users', userId, 'classes', classId, 'generalAssignments', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);
    
    if (!assignmentSnap.exists()) {
      console.warn('과제를 찾을 수 없습니다:', assignmentId);
      return;
    }
    
    const assignmentData = assignmentSnap.data() as Assignment;
    const assignedStudentIds = assignmentData.assignedStudentIds || [];
    
    // 과제에 할당된 멤버들만 필터링
    const assignedMembers = currentMembers.filter(member => assignedStudentIds.includes(member.id));
    
    // V3 구조로 상태 동기화
    const statusRef = doc(db, 'users', userId, 'classes', classId, 'generalAssignmentStatuses', assignmentId);
    const statusSnap = await getDoc(statusRef);
    
    let studentStatuses = statusSnap.exists() ? (statusSnap.data().studentStatuses || []) : [];
    
    // 현재 할당된 멤버들의 상태가 있는지 확인하고 없으면 추가
    for (const member of assignedMembers) {
      const memberStatusIndex = studentStatuses.findIndex((s: any) => s.studentId === member.id);
      if (memberStatusIndex === -1) {
        studentStatuses.push({
          studentId: member.id,
          status: 'pending' as const,
          earnedPoints: 0,
          updatedAt: new Date()
        });
      }
    }
    
    // 할당되지 않은 멤버들의 상태 제거
    studentStatuses = studentStatuses.filter((s: any) => assignedStudentIds.includes(s.studentId));
    
    // 상태 업데이트 (메타데이터 포함)
    await setDoc(statusRef, {
      assignmentId,
      studentStatuses,
      lastUpdated: new Date(),
      classId,
      // 테이블 로드를 위한 메타데이터 추가
      categoryId: assignmentData.categoryId,
      dueDate: assignmentData.dueDate,
      title: assignmentData.title,
      assignedStudentIds: assignmentData.assignedStudentIds
    });
  },

  async deleteAssignment(
    userId: string,
    classId: string,
    assignmentId: string,
    memberGroup?: string
  ): Promise<void> {
    // 새로운 통합 구조 사용
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId, memberGroup);
    
    try {
      // 과제 정보 조회 (제목 필요)
      const assignmentInfo = await consolidatedService.loadGeneralAssignment(assignmentId);
      const assignmentTitle = assignmentInfo?.title || 'Unknown Assignment';
      
      // 통합 문서에서 과제 삭제
      await consolidatedService.deleteGeneralAssignment(assignmentId);
      
      // 🎯 멤버에서 해당 과제 포인트 제거
      try {
        await classMembersService.removeAssignmentPoints(userId, classId, assignmentId, assignmentTitle);
      } catch (error) {
        console.error('과제 포인트 제거 실패:', error);
        // 포인트 제거 실패해도 과제 삭제는 성공한 상태이므로 에러를 다시 던지지 않음
      }
      
      console.log('✅ 새로운 구조: 일반 과제 삭제 완료:', assignmentId);
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 삭제 실패:', error);
      throw error;
    }
  },

  // 멤버 상태 로드 (새로운 통합 구조)
  async loadStudentStatuses(userId: string, classId: string, assignmentId: string): Promise<AssignmentStudentStatus[]> {
    // 새로운 통합 구조 사용
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId);
    
    try {
      const assignmentInfo = await consolidatedService.loadGeneralAssignment(assignmentId);
      
      if (!assignmentInfo) {
        return [];
      }

      const memoMap = await assignmentMemoService.getMemoMapForAssignment(userId, classId, assignmentId);

      // 멤버 상태들을 AssignmentStudentStatus 형태로 변환
      const statuses: AssignmentStudentStatus[] = assignmentInfo.studentStatuses.map(status => {
        const statusAny = status as any;
        const memoEntry = memoMap?.[status.studentId];
        
        // 제출 확인 데이터 디버깅
        if (statusAny.submissionLink || statusAny.submissionMessage) {
          console.log(`🔍 멤버 ${status.studentId}의 제출 확인 데이터 로드:`, {
            submissionLink: statusAny.submissionLink,
            submissionMessage: statusAny.submissionMessage,
            submittedAt: statusAny.submittedAt
          });
        }
        
        return {
          id: status.studentId,
          studentId: status.studentId,
          assignmentId: assignmentId,
          status: status.status,
          earnedPoints: status.points,
          memo: memoEntry?.memo || status.memo || '',
          submissionLink: statusAny.submissionLink,
          submissionMessage: statusAny.submissionMessage,
          submissionImagePath: statusAny.submissionImagePath,
          submissionImageUpdatedAt: statusAny.submissionImageUpdatedAt,
          submissionImageSizeBytes: statusAny.submissionImageSizeBytes,
          submittedAt: statusAny.submittedAt,
          updatedAt: status.updatedAt,
          confirmedAt: status.status === 'confirmed' ? status.updatedAt : undefined
        };
      });

      console.log('✅ 새로운 구조: 멤버 상태 로드 완료:', assignmentId);
      return statuses;
    } catch (error) {
      console.error('❌ 새로운 구조: 멤버 상태 로드 실패:', error);
      return [];
    }
  },

  // 멤버 상태 저장 (V3 구조 - 배열로 저장)
  async saveStudentStatuses(userId: string, classId: string, assignmentId: string, statuses: AssignmentStudentStatus[]): Promise<void> {
    try {
      console.log('저장 시작:', { userId, classId, assignmentId, statusesCount: statuses.length });
      
      const statusRef = doc(db, 'users', userId, 'classes', classId, 'generalAssignmentStatuses', assignmentId);
      
      // 모든 멤버 상태를 배열로 변환 (memo는 assignmentMemo 컬렉션에서 관리)
      const studentStatuses = statuses.map(status => ({
        studentId: status.studentId,
        status: status.status,
        earnedPoints: status.earnedPoints || 0,
        updatedAt: new Date()
      }));
      
      await setDoc(statusRef, {
        assignmentId,
        studentStatuses,
        lastUpdated: new Date(),
        classId
      });
      
      console.log('저장 완료');
    } catch (error) {
      console.error('멤버 상태 저장 실패:', error);
      throw error;
    }
  },

  // 멤버 삭제 시 연관된 모든 과제 데이터 정리
  async cleanupStudentAssignmentData(userId: string, classId: string, studentIds: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);

      // 모든 일반 과제의 멤버 상태 데이터 삭제 및 assignedStudentIds 업데이트
      const generalAssignmentsRef = collection(db, 'users', userId, 'classes', classId, 'generalAssignments');
      const generalAssignmentsSnapshot = await getDocs(generalAssignmentsRef);

      for (const assignmentDoc of generalAssignmentsSnapshot.docs) {
        const assignmentId = assignmentDoc.id;
        const assignmentData = assignmentDoc.data();

        // 현재 과제의 assignedStudentIds 확인
        const currentAssignedIds = assignmentData.assignedStudentIds || [];

        // 삭제할 멤버들을 제외한 새로운 assignedStudentIds 생성
        const updatedAssignedIds = currentAssignedIds.filter((id: string) => !studentIds.includes(id));

        if (updatedAssignedIds.length !== currentAssignedIds.length) {
          // assignedStudentIds가 변경된 경우에만 업데이트
          const assignmentRef = doc(db, 'users', userId, 'classes', classId, 'generalAssignments', assignmentId);
          batch.update(assignmentRef, {
            assignedStudentIds: updatedAssignedIds,
            updatedAt: new Date()
          });
        }

        // V3 구조에서 studentStatuses 배열에서 삭제할 멤버 제거
        const statusRef = doc(db, 'users', userId, 'classes', classId, 'generalAssignmentStatuses', assignmentId);
        const statusSnap = await getDoc(statusRef);
        
        if (statusSnap.exists()) {
          const statusData = statusSnap.data();
          const studentStatuses = statusData.studentStatuses || [];
          
          // 삭제할 멤버들을 제외한 새로운 studentStatuses 생성
          const updatedStudentStatuses = studentStatuses.filter((status: any) => 
            !studentIds.includes(status.studentId)
          );
          
          if (updatedStudentStatuses.length !== studentStatuses.length) {
            batch.update(statusRef, {
              studentStatuses: updatedStudentStatuses,
              lastUpdated: new Date()
            });
          }
        }
      }

      // 배치 커밋
      await batch.commit();
    } catch (error) {
      console.error('멤버 과제 데이터 정리 실패:', error);
      throw error;
    }
  },

  /**
   * 최적화된 멤버 상태 업데이트 (새로운 배열 기반 구조)
   */
  async updateStudentStatusOptimized(
    userId: string,
    classId: string,
    assignmentId: string,
    studentId: string,
    status: AssignmentStatusValue,
    earnedPoints: number = 0,
    memo: string = ''
  ): Promise<void> {
    try {
      await optimizedAssignmentServiceV3.updateAssignmentStudentStatus(
        userId,
        classId,
        assignmentId,
        studentId,
        status,
        earnedPoints,
        memo
      );
      console.log(`✅ 최적화된 멤버 상태 업데이트 완료: ${assignmentId} - ${studentId} - ${status}`);
    } catch (error) {
      console.error('❌ 최적화된 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  },

  /**
   * 최적화된 여러 멤버 상태 일괄 업데이트 (새로운 배열 기반 구조)
   */
  async batchUpdateStudentStatusesOptimized(
    userId: string,
    classId: string,
    assignmentId: string,
    studentUpdates: Array<{
      studentId: string;
      status: AssignmentStatusValue;
      earnedPoints: number;
      memo?: string;
      submissionLink?: string;
      submissionMessage?: string;
      submittedAt?: Date;
      submissionImagePath?: string;
      submissionImageUpdatedAt?: string;
      submissionImageSizeBytes?: number;
    }>
  ): Promise<void> {
    try {
      await optimizedAssignmentServiceV3.batchUpdateAssignmentStudentStatuses(
        userId,
        classId,
        assignmentId,
        studentUpdates
      );
      console.log(`✅ 최적화된 멤버 상태 일괄 업데이트 완료: ${assignmentId} - ${studentUpdates.length}명`);
    } catch (error) {
      console.error('❌ 최적화된 멤버 상태 일괄 업데이트 실패:', error);
      throw error;
    }
  }
};

